import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import Layout from './components/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PWAProvider } from './contexts/PWAContext';
import ProtectedRoute from './components/ProtectedRoute';
import PWAPrompts from './components/PWAPrompts';

// Route-level code splitting (P3): each page loads on demand instead of shipping
// one 530KB bundle (recharts, calendar, virtuoso...) to the login screen.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PendingOperations = lazy(() => import('./pages/PendingOperations'));
const History = lazy(() => import('./pages/History'));
const TorrentsPage = lazy(() => import('./pages/Torrents'));
const Settings = lazy(() => import('./pages/Settings'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const Discover = lazy(() => import('./pages/Discover'));
const StorageAnalytics = lazy(() => import('./pages/StorageAnalytics'));
const HealthMonitoring = lazy(() => import('./pages/HealthMonitoring'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Subtitles = lazy(() => import('./pages/Subtitles'));
const ImportFromDisk = lazy(() => import('./pages/ImportFromDisk'));

const PageFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

function AppContent() {
  const { theme } = useTheme();

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <PWAProvider>
        <AuthProvider>
          <Router>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  borderRadius: '8px',
                },
              }}
            />
            <Suspense fallback={PageFallback}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="pending" element={<PendingOperations />} />
                  <Route path="import" element={<ImportFromDisk />} />
                  <Route path="history" element={<History />} />
                  <Route path="torrents" element={<TorrentsPage />} />
                  <Route path="watchlist" element={<Watchlist />} />
                  <Route path="discover" element={<Discover />} />
                  <Route path="calendar" element={<Calendar />} />
                  <Route path="subtitles" element={<Subtitles />} />
                  <Route path="storage" element={<StorageAnalytics />} />
                  <Route path="health" element={<HealthMonitoring />} />
                  <Route path="settings" element={<Settings />} />
                </Route>
              </Routes>
            </Suspense>
            <PWAPrompts />
          </Router>
        </AuthProvider>
      </PWAProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;